/**
 * WizardCreationController — page `/factures-assistant.html` (EPIC-011 / 11.4).
 *
 * Wizard pas-à-pas de création d'un document commercial (devis, facture,
 * acompte, avoir, récurrence). Couvre en 11.4 les slides 1 (type), 1bis
 * (sélection facture source pour avoir), 2 (client), 4 (lignes + totaux +
 * PJ). Les slides 3 (suggestions) et 5 (récurrence) sont stub côté UI et
 * couvertes par EPIC-011.5.
 *
 * Persistance différée (décision D2 EPIC-011) : aucune écriture serveur
 * tant que l'utilisateur ne clique pas « Enregistrer brouillon » ou
 * « Valider et continuer ». L'état du wizard est tenu en `sessionStorage`
 * sous la clé `wizard-draft-v1`.
 *
 * Reprise verbatim de la maquette `maquettes/backoffice/factures-wizard.html`
 * refondue en 11.1. Aucune classe inventée. Templates `<template>` clonés
 * via {@link cloneTemplate} + {@link fillSlots} (zéro HTML inline en JS,
 * cf. CLAUDE.md du projet).
 *
 * URL canonique (D10) : 5 portes d'entrée mappées vers le même page
 * `factures-wizard.html` via `serve.js`. Le controller lit
 * `window.location.pathname` pour pré-sélectionner le type slide 1.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const STORAGE_KEY = "wizard-draft-v1";
const SEARCH_DEBOUNCE_MS = 300;
const SOURCE_SEARCH_DEBOUNCE_MS = 300;
const SAVE_DEBOUNCE_MS = 200;
const PREVIEW_DEBOUNCE_MS = 300;
const FRENCH_MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

// Mapping URL → type slide 1 (D10).
const PATH_TO_TYPE = {
  "/devis/nouveau": "quote",
  "/factures/nouveau": "invoice",
  "/factures-acompte/nouveau": "advance_payment",
  "/avoirs/nouveau": "credit_note",
  "/factures-recurrentes/nouveau": "recurring",
};

// Mapping type wizard → kind backend (POST /invoices/draft) ou route
// dédiée (POST /quotes/draft pour quote).
const TYPE_TO_INVOICE_KIND = {
  invoice: "standard",
  advance_payment: "advance_payment",
  credit_note: "credit_note",
};

// Étape index sur le step header (alignée avec data-wizard-creation-step-param).
// 0=type, "1bis"=source, 1=client, 2=suggestions, 3=lines, 4=recurrence
const STEP_TYPE = 0;
const STEP_SOURCE = "1bis";
const STEP_CLIENT = 1;
const STEP_SUGGESTIONS = 2;
const STEP_LINES = 3;
const STEP_RECURRENCE = 4;

// Taux TVA autorisés (cf. EPIC-010.2 — liste fermée FR).
const VAT_RATES = [0, 2.1, 5.5, 10, 20];

function newLineId() {
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatEuros(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "0,00 €";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDateFR(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function formatRecurrenceDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getUTCDate()} ${FRENCH_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export default class WizardCreationController extends BaseController {
  static targets = [
    "root", "progress",
    "prevBtn", "nextBtn", "saveDraftBtn", "submitBtn",
    "stepItem", "stepBtn", "panel",
    // Slide 1
    "typeInput", "typeLabel",
    // Slide 1bis (avoir source)
    "sourceSearch", "sourceResults", "sourceHint",
    // Slide 2 (client)
    "clientSearch", "clientResults",
    "selectedClient", "selectedClientName", "selectedClientBadges",
    "newClientToggle", "newClientForm",
    "newClientType", "newClientName",
    "newClientSiretField", "newClientSiret",
    "newClientError", "newClientSubmit",
    // Slide 4 (lignes)
    "articleSearch", "articleResults",
    "newArticleToggle", "newArticleForm",
    "newArticleLabel", "newArticleUnit", "newArticlePrice", "newArticleVat",
    "newArticleActivityField", "newArticleActivity", "newArticleError",
    "linesContainer",
    "totalsHt", "totalsVatContainer", "totalsVatBreakdown", "totalsTtc",
    "totalsActivityBreakdownCard", "totalsActivityBreakdown",
    // PJ
    "attachmentsList", "attachmentsOpenModalBtn",
    "documentsModal", "documentsModalSearch", "documentsModalList",
    // Slide 3 (suggestions)
    "topArticlesList", "lastDocumentsList",
    // Slide 5 (récurrence)
    "recurrenceIntervalMonths", "recurrenceDayOfMonth",
    "recurrenceStartDate", "recurrenceEndDate",
    "recurrenceMode", "recurrencePayment", "recurrenceReminders",
    "recurrenceAttachments", "previewDates", "recurrenceError",
  ];

  connect() {
    // État interne
    this.state = {
      type: "invoice",                  // quote | invoice | advance_payment | credit_note | recurring
      sourceInvoiceId: null,            // pour kind=credit_note
      sourceInvoiceLabel: null,
      clientId: null,
      clientLabel: null,
      lines: [],                        // [{id, articleId?, activityId, label, qty, unitPrice, vatRate}]
      attachedDocumentIds: new Set(),
      currentStep: STEP_TYPE,
      // Slide 3 — suggestions (EPIC-011.5)
      suggestions: { topArticles: [], lastDocuments: [], skip: false },
      // Slide 5 — récurrence (EPIC-011.5)
      recurrence: {
        intervalMonths: 1,
        dayOfMonth: 15,
        startDate: "",
        endDate: "",
        generationMode: "draft",
        integratedPayment: true,
        autoReminders: true,
        attachmentsMode: "recalculate",
        frozenAttachmentIds: [],
      },
    };

    // Caches API
    this.activitiesById = new Map();    // id → activity object
    this.activeActivities = [];
    this.documentsById = new Map();     // id → doc object
    this.defaultAttachments = { invoices: [], quotes: [] };

    // Selected client detail (avec paymentScore)
    this.selectedClientDetail = null;

    // Timers
    this._saveTimer = null;
    this._clientSearchTimer = null;
    this._articleSearchTimer = null;
    this._sourceSearchTimer = null;
    this._previewTimer = null;

    // Settings org pour pré-remplissage slide 5 (intégration paiement)
    this._orgSettingsLoaded = false;

    // Init from URL
    const pathType = PATH_TO_TYPE[window.location.pathname];
    if (pathType) {
      this.state.type = pathType;
    }

    // Initialize UI from preset type
    this._syncTypeRadios();
    this._renderTotals();

    // Async init : load activities + documents, then optionally restore session
    this._initAsync();
  }

  disconnect() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    if (this._clientSearchTimer) clearTimeout(this._clientSearchTimer);
    if (this._articleSearchTimer) clearTimeout(this._articleSearchTimer);
    if (this._sourceSearchTimer) clearTimeout(this._sourceSearchTimer);
    if (this._previewTimer) clearTimeout(this._previewTimer);
  }

  async _initAsync() {
    try {
      const [activities, docsResp] = await Promise.all([
        api.get("/activities", { "filter[status]": "active" }).catch(() => ({ data: [] })),
        api.get("/organization/documents").catch(() => ({ documents: [], defaultAttachments: { invoices: [], quotes: [] } })),
      ]);

      const activityList = Array.isArray(activities.data) ? activities.data : (activities.data || []);
      this.activeActivities = activityList;
      activityList.forEach((a) => this.activitiesById.set(a.id, a));

      const documents = docsResp.documents || [];
      documents.forEach((d) => this.documentsById.set(d.id, d));
      this.defaultAttachments = docsResp.defaultAttachments || { invoices: [], quotes: [] };

      this._populateNewArticleActivities();
      this._applyDefaultAttachments();
      this._renderAttachmentsList();
      this._renderDocumentsModalList();
    } catch (e) {
      this.showError("Impossible de charger les activités et documents de l'organization.");
    }

    // Restoration session
    const stored = this._readStorage();
    if (stored) {
      const restore = await this.confirm(
        "Vous avez un brouillon en cours dans cet onglet. Le reprendre ?",
        { confirmLabel: "Reprendre", cancelLabel: "Recommencer" },
      );
      if (restore) {
        this._restoreState(stored);
      } else {
        this._clearStorage();
      }
    }

    this._renderStepHeader();
    this._renderPanels();
    this._syncRecurrenceInputs();
    if (this.state.type === "recurring") {
      this._fetchOrgSettingsForRecurrence();
      this._schedulePreviewDates();
    }
  }

  // ===========================================================================
  // Persistance sessionStorage
  // ===========================================================================

  _scheduleSave() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => this._persistState(), SAVE_DEBOUNCE_MS);
  }

  _persistState() {
    const payload = {
      type: this.state.type,
      sourceInvoiceId: this.state.sourceInvoiceId,
      sourceInvoiceLabel: this.state.sourceInvoiceLabel,
      clientId: this.state.clientId,
      clientLabel: this.state.clientLabel,
      lines: this.state.lines,
      attachedDocumentIds: Array.from(this.state.attachedDocumentIds),
      currentStep: this.state.currentStep,
      recurrence: this.state.recurrence,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // Quota / privacy mode → silent skip
    }
  }

  _readStorage() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  }

  _clearStorage() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  }

  _restoreState(stored) {
    this.state.type = stored.type || "invoice";
    this.state.sourceInvoiceId = stored.sourceInvoiceId || null;
    this.state.sourceInvoiceLabel = stored.sourceInvoiceLabel || null;
    this.state.clientId = stored.clientId || null;
    this.state.clientLabel = stored.clientLabel || null;
    this.state.lines = Array.isArray(stored.lines) ? stored.lines : [];
    this.state.attachedDocumentIds = new Set(stored.attachedDocumentIds || []);
    this.state.currentStep = stored.currentStep ?? STEP_TYPE;
    if (stored.recurrence && typeof stored.recurrence === "object") {
      this.state.recurrence = { ...this.state.recurrence, ...stored.recurrence };
      if (!Array.isArray(this.state.recurrence.frozenAttachmentIds)) {
        this.state.recurrence.frozenAttachmentIds = [];
      }
    }

    this._syncTypeRadios();
    this._renderLines();
    this._renderTotals();
    this._renderAttachmentsList();
    this._renderDocumentsModalList();
    this._syncRecurrenceInputs();
    if (this.state.clientId && this.state.clientLabel) {
      this._renderSelectedClient({ legalName: this.state.clientLabel });
      this._fetchClientDetail(this.state.clientId);
      this._fetchSuggestionsForClient(this.state.clientId);
    }
  }

  // ===========================================================================
  // Navigation (steps)
  // ===========================================================================

  _visibleStepKeys() {
    // Ordre canonique : type → [source si avoir] → client → [suggestions si client a historique] → lines → [recurrence si récurrence]
    const keys = [STEP_TYPE];
    if (this.state.type === "credit_note") keys.push(STEP_SOURCE);
    keys.push(STEP_CLIENT);
    // Slide 3 sautée pour les avoirs (héritage 1bis) ou si l'historique du
    // client retourne 0 article ET 0 document (spec §05 + EPIC-011.5).
    if (this.state.type !== "credit_note" && !this.state.suggestions.skip) {
      keys.push(STEP_SUGGESTIONS);
    }
    keys.push(STEP_LINES);
    if (this.state.type === "recurring") keys.push(STEP_RECURRENCE);
    return keys;
  }

  _renderStepHeader() {
    const visible = this._visibleStepKeys();
    const total = visible.length;

    this.stepItemTargets.forEach((item) => {
      const key = item.dataset.wizardStepKey;
      const stepBtn = item.querySelector("[data-wizard-creation-target='stepBtn']");
      const stepValue = stepBtn?.dataset.wizardCreationStepParam;

      let visibleStep = false;
      if (key === "source") visibleStep = visible.includes(STEP_SOURCE);
      else if (key === "recurrence") visibleStep = visible.includes(STEP_RECURRENCE);
      else if (stepValue !== undefined) {
        const parsed = stepValue === "1bis" ? STEP_SOURCE : Number(stepValue);
        visibleStep = visible.includes(parsed);
      }
      item.hidden = !visibleStep;
      if (stepBtn) stepBtn.hidden = !visibleStep;
    });

    const visibleIndex = visible.indexOf(this.state.currentStep);
    const safeIndex = visibleIndex >= 0 ? visibleIndex : 0;

    this.stepItemTargets.forEach((item) => {
      const stepBtn = item.querySelector("[data-wizard-creation-target='stepBtn']");
      const stepValue = stepBtn?.dataset.wizardCreationStepParam;
      const parsed = stepValue === "1bis" ? STEP_SOURCE : Number(stepValue);
      const idx = visible.indexOf(parsed);

      item.classList.toggle("is-current", idx === safeIndex);
      item.classList.toggle("is-done", idx >= 0 && idx < safeIndex);

      if (stepBtn) {
        if (idx === safeIndex) stepBtn.setAttribute("aria-current", "step");
        else stepBtn.removeAttribute("aria-current");
        stepBtn.disabled = idx > safeIndex;
      }
    });

    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `Étape ${safeIndex + 1} sur ${total}`;
    }

    const isLast = safeIndex === total - 1;
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = safeIndex === 0;
    if (this.hasNextBtnTarget) this.nextBtnTarget.hidden = isLast;
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.hidden = !isLast;
  }

  _renderPanels() {
    const current = this.state.currentStep;
    this.panelTargets.forEach((panel) => {
      const key = panel.dataset.wizardCreationPanelKey;
      let isActive = false;
      switch (key) {
        case "type":         isActive = current === STEP_TYPE; break;
        case "source":       isActive = current === STEP_SOURCE; break;
        case "client":       isActive = current === STEP_CLIENT; break;
        case "suggestions":  isActive = current === STEP_SUGGESTIONS; break;
        case "lines":        isActive = current === STEP_LINES; break;
        case "recurrence":   isActive = current === STEP_RECURRENCE; break;
      }
      panel.classList.toggle("is-active", isActive);
      panel.hidden = !isActive && (key === "source" || key === "recurrence")
        ? !isActive
        : false;
      // Pour les panels qui doivent rester dans le DOM mais cachés visuellement
      // quand `is-active=false`, on s'appuie sur la classe `is-active` du CSS.
      if (key === "source" && !this._visibleStepKeys().includes(STEP_SOURCE)) {
        panel.hidden = true;
      }
      if (key === "recurrence" && !this._visibleStepKeys().includes(STEP_RECURRENCE)) {
        panel.hidden = true;
      }
    });
  }

  goToStep(event) {
    const raw = event.currentTarget?.dataset.wizardCreationStepParam;
    const parsed = raw === "1bis" ? STEP_SOURCE : Number(raw);
    const visible = this._visibleStepKeys();
    if (!visible.includes(parsed)) return;
    const idxCurrent = visible.indexOf(this.state.currentStep);
    const idxTarget = visible.indexOf(parsed);
    if (idxTarget > idxCurrent) return; // pas de saut en avant
    this.state.currentStep = parsed;
    this._renderStepHeader();
    this._renderPanels();
    this._scheduleSave();
  }

  next() {
    const visible = this._visibleStepKeys();
    const idx = visible.indexOf(this.state.currentStep);
    if (idx < 0 || idx >= visible.length - 1) return;
    this.state.currentStep = visible[idx + 1];
    this._renderStepHeader();
    this._renderPanels();
    this._scheduleSave();
  }

  prev() {
    const visible = this._visibleStepKeys();
    const idx = visible.indexOf(this.state.currentStep);
    if (idx <= 0) return;
    this.state.currentStep = visible[idx - 1];
    this._renderStepHeader();
    this._renderPanels();
    this._scheduleSave();
  }

  // ===========================================================================
  // Slide 1 — Type
  // ===========================================================================

  _syncTypeRadios() {
    this.typeInputTargets.forEach((input) => {
      input.checked = input.value === this.state.type;
    });
    this._restyleTypeLabels();
  }

  _restyleTypeLabels() {
    this.typeLabelTargets.forEach((lbl) => {
      const input = lbl.querySelector("input[type='radio']");
      if (input && input.checked) {
        lbl.style.border = "1px solid var(--accent)";
        lbl.style.background = "var(--accent-soft)";
      } else {
        lbl.style.border = "1px solid var(--border)";
        lbl.style.background = "";
      }
    });
  }

  typeChanged(event) {
    const target = event.target;
    if (!target || target.type !== "radio") return;
    const previous = this.state.type;
    this.state.type = target.value;

    // Si on bascule depuis credit_note → reset de la source
    if (previous === "credit_note" && this.state.type !== "credit_note") {
      this.state.sourceInvoiceId = null;
      this.state.sourceInvoiceLabel = null;
    }

    // Si on bascule depuis ou vers credit_note → recalcule du skip
    // suggestions (les avoirs sautent toujours la slide 3).
    if (previous !== this.state.type && this.state.clientId) {
      this._fetchSuggestionsForClient(this.state.clientId);
    } else if (this.state.type === "credit_note") {
      this.state.suggestions.skip = true;
    }

    // Si on bascule vers récurrence → pré-charge les settings org +
    // synchronise les inputs slide 5 (min date dynamique).
    if (this.state.type === "recurring") {
      this._syncRecurrenceInputs();
      if (!this._orgSettingsLoaded) this._fetchOrgSettingsForRecurrence();
      this._schedulePreviewDates();
    }

    this._restyleTypeLabels();
    this._renderStepHeader();
    this._renderPanels();
    this._scheduleSave();
  }

  // ===========================================================================
  // Slide 1bis — Sélection facture source (avoir)
  // ===========================================================================

  sourceSearchInput(event) {
    if (this._sourceSearchTimer) clearTimeout(this._sourceSearchTimer);
    const q = event.target.value.trim();
    this._sourceSearchTimer = setTimeout(() => this._fetchSourceInvoices(q), SOURCE_SEARCH_DEBOUNCE_MS);
  }

  async _fetchSourceInvoices(q) {
    try {
      const params = { status: "issued", perPage: "10" };
      if (q) params.q = q;
      const resp = await api.get("/invoices", params);
      const items = Array.isArray(resp.data) ? resp.data : [];
      this._renderSourceResults(items);
    } catch (e) {
      this.showError("Recherche facture source impossible.");
    }
  }

  _renderSourceResults(items) {
    if (!this.hasSourceResultsTarget) return;
    this.sourceResultsTarget.innerHTML = "";
    if (items.length === 0) {
      const empty = document.createElement("li");
      empty.className = "fs-12 text-muted";
      empty.style.padding = "12px 0";
      empty.textContent = "Aucune facture émise ne correspond.";
      this.sourceResultsTarget.appendChild(empty);
      return;
    }
    items.forEach((inv) => {
      const frag = this.cloneTemplate("tpl-source-invoice-row");
      const header = `${inv.number || "(sans numéro)"} · ${formatEuros(inv.amountInclVat)} TTC`;
      const meta = `Émise le ${formatDateFR(inv.dateEmission)}`;
      const lines = inv.client?.legalName ? `Client : ${inv.client.legalName}` : "";
      this.fillSlots(frag, { header, meta, lines });
      const radio = frag.querySelector("input[type='radio']");
      if (radio) {
        radio.value = inv.id;
        radio.dataset.label = header;
      }
      this.sourceResultsTarget.appendChild(frag);
    });
  }

  async sourceSelected(event) {
    const target = event.target;
    if (!target || target.type !== "radio") return;
    const invoiceId = target.value;
    if (!invoiceId) return;
    try {
      const resp = await api.get(`/invoices/${invoiceId}/lines-for-credit-note`);
      const data = resp.data || resp;
      this.state.sourceInvoiceId = invoiceId;
      this.state.sourceInvoiceLabel = target.dataset.label || invoiceId;
      this.state.clientId = data.clientId;
      this.state.lines = (data.lines || []).map((l) => ({
        id: newLineId(),
        articleId: l.articleId || null,
        activityId: l.activityId,
        label: l.label,
        qty: l.quantity,
        unitPrice: l.priceExclVat,
        unit: l.unit,
        vatRate: l.vatRate,
      }));
      this._scheduleSave();
      // Préfille le client
      try {
        const clientResp = await api.get(`/clients/${data.clientId}`);
        const client = clientResp.data || clientResp;
        this.state.clientLabel = client.legalName;
        this._renderSelectedClient(client);
      } catch {
        // Anti-leak / archivé : on garde l'ID mais sans badges
      }
      this._renderLines();
      this._renderTotals();
      // Saute directement à slide 4
      this.state.currentStep = STEP_LINES;
      this._renderStepHeader();
      this._renderPanels();
    } catch (e) {
      this.showError("Impossible de récupérer les lignes de cette facture.");
    }
  }

  // ===========================================================================
  // Slide 2 — Client
  // ===========================================================================

  clientSearchInput(event) {
    if (this._clientSearchTimer) clearTimeout(this._clientSearchTimer);
    const q = event.target.value.trim();
    if (q.length < 3) {
      this._hideClientResults();
      return;
    }
    this._clientSearchTimer = setTimeout(() => this._fetchClients(q), SEARCH_DEBOUNCE_MS);
  }

  clientSearchBlur() {
    // Léger délai pour laisser le clic se propager
    setTimeout(() => this._hideClientResults(), 150);
  }

  clientSearchKeydown(event) {
    if (!this.hasClientResultsTarget || this.clientResultsTarget.hidden) return;
    const options = Array.from(this.clientResultsTarget.querySelectorAll("[role='option']"));
    if (options.length === 0) return;
    const currentIdx = options.findIndex((o) => o.getAttribute("aria-selected") === "true");

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = (currentIdx + 1) % options.length;
      this._highlightOption(options, next);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const next = (currentIdx - 1 + options.length) % options.length;
      this._highlightOption(options, next);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const sel = options[currentIdx >= 0 ? currentIdx : 0];
      if (sel) sel.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    } else if (event.key === "Escape") {
      this._hideClientResults();
    }
  }

  _highlightOption(options, index) {
    options.forEach((o, i) => {
      const selected = i === index;
      o.setAttribute("aria-selected", selected ? "true" : "false");
      o.style.background = selected ? "var(--surface-2)" : "";
    });
  }

  async _fetchClients(q) {
    // Le carnet interne et l'annuaire public sont interrogés en parallèle
    // (99.91). L'annuaire est silencieusement ignoré s'il échoue (offline /
    // 429) ou si la saisie ressemble à un email (l'annuaire n'indexe que des
    // entités à SIRET).
    const isEmailLike = q.includes("@");
    const [carnet, annuaire] = await Promise.allSettled([
      api.get("/clients", { q, perPage: "10", "filter[status]": "active" }),
      isEmailLike ? Promise.resolve([]) : this._fetchAnnuaire(q),
    ]);

    if (carnet.status !== "fulfilled") {
      this.showError("Recherche client impossible.");
      return;
    }
    const clients = Array.isArray(carnet.value.data) ? carnet.value.data : [];
    const annuaireResults = annuaire.status === "fulfilled" ? annuaire.value : [];
    this._renderClientResults(clients, annuaireResults);
  }

  async _fetchAnnuaire(q) {
    const url = `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(q)}&per_page=8&minimal=true&include=siege`;
    const response = await fetch(url, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.results) ? data.results : [];
  }

  _renderClientResults(clients, annuaireResults = []) {
    if (!this.hasClientResultsTarget) return;
    this.clientResultsTarget.innerHTML = "";

    // Dédoublonnage : masquer toute proposition annuaire dont le SIRET est déjà
    // listé dans le carnet au-dessus.
    const carnetSirets = new Set(
      clients.map((c) => this._siretDigits(c.siret)).filter((s) => s.length === 14),
    );
    const annuaireItems = (annuaireResults || []).filter((r) => {
      const siret = this._siretDigits(r?.siege?.siret || "");
      return siret.length === 14 && !carnetSirets.has(siret);
    });

    // Clients du carnet d'abord.
    clients.forEach((c) => {
      const frag = this.cloneTemplate("tpl-client-result");
      const meta = [c.type === "b2b" ? "Entreprise" : c.type === "b2c" ? "Particulier" : "Administration"];
      if (c.siret) meta.push(`SIRET ${c.siret}`);
      if (c.siren) meta.push(`SIREN ${c.siren}`);
      this.fillSlots(frag, { name: c.legalName, meta: meta.join(" · ") });
      const li = frag.querySelector("li");
      if (li) {
        li.dataset.clientId = c.id;
        li.addEventListener("mousedown", (ev) => {
          ev.preventDefault();
          this._selectClient(c);
        });
      }
      this.clientResultsTarget.appendChild(frag);
    });

    // Propositions annuaire (création immédiate à la sélection).
    if (annuaireItems.length > 0) {
      this.clientResultsTarget.appendChild(this.cloneTemplate("tpl-client-annuaire-separator"));
      annuaireItems.forEach((r) => {
        const frag = this.cloneTemplate("tpl-client-annuaire-result");
        const siege = r?.siege || {};
        const siret = siege.siret || "";
        const name = r?.nom_raison_sociale || r?.nom_complet || "—";
        const metaParts = [];
        if (siege.libelle_commune) metaParts.push(siege.libelle_commune);
        if (siret) metaParts.push(`SIRET ${this._formatSiret(siret)}`);
        this.fillSlots(frag, { name, meta: metaParts.join(" · ") });
        const li = frag.querySelector("li");
        if (li) {
          li.addEventListener("mousedown", (ev) => {
            ev.preventDefault();
            this._selectAnnuaireSuggestion(r);
          });
        }
        this.clientResultsTarget.appendChild(frag);
      });
    }

    // État vide : ni carnet ni annuaire.
    if (clients.length === 0 && annuaireItems.length === 0) {
      const empty = document.createElement("li");
      empty.className = "fs-12 text-muted";
      empty.style.padding = "12px 14px";
      empty.style.border = "1px solid var(--border)";
      empty.textContent = "Aucun client correspondant. Cliquez sur « + Créer un nouveau client » pour en ajouter un.";
      this.clientResultsTarget.appendChild(empty);
    }

    this.clientResultsTarget.hidden = false;
    this.clientSearchTarget.setAttribute("aria-expanded", "true");
  }

  async _selectAnnuaireSuggestion(result) {
    const siege = result?.siege || {};
    const siret = this._siretDigits(siege.siret || "");
    const legalName = result?.nom_raison_sociale || result?.nom_complet || "";
    if (!legalName || siret.length !== 14) {
      this.showError("Proposition annuaire incomplète, création impossible.");
      return;
    }
    // Type déduit de la catégorie juridique INSEE : code « 7 » = personne
    // morale de droit public → administration, sinon entreprise (jamais b2c).
    const type = this._deduceClientType(result);
    this._hideClientResults();
    if (this.hasClientSearchTarget) this.clientSearchTarget.value = "";
    try {
      const resp = await api.post("/clients", { type, legalName, siret });
      const client = resp.data || resp;
      this.showSuccess(`Client « ${client.legalName} » créé.`);
      this._selectClient(client);
    } catch (e) {
      const msg = e instanceof APIError ? e.message : "Création du client depuis l'annuaire impossible.";
      this.showError(msg);
    }
  }

  _deduceClientType(result) {
    const natureCode = String(result?.nature_juridique || "");
    return natureCode.startsWith("7") ? "public_administration" : "b2b";
  }

  _siretDigits(value) {
    return (value || "").replace(/\D/g, "");
  }

  _formatSiret(siret) {
    const digits = (siret || "").replace(/\D/g, "");
    if (digits.length !== 14) return digits;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }

  _hideClientResults() {
    if (!this.hasClientResultsTarget) return;
    this.clientResultsTarget.hidden = true;
    if (this.hasClientSearchTarget) this.clientSearchTarget.setAttribute("aria-expanded", "false");
  }

  async _selectClient(client) {
    this.state.clientId = client.id;
    this.state.clientLabel = client.legalName;
    this._hideClientResults();
    if (this.hasClientSearchTarget) this.clientSearchTarget.value = "";
    this._renderSelectedClient(client);
    this._scheduleSave();
    await Promise.all([
      this._fetchClientDetail(client.id),
      this._fetchSuggestionsForClient(client.id),
    ]);
  }

  async _fetchClientDetail(clientId) {
    try {
      const resp = await api.get(`/clients/${clientId}`);
      const client = resp.data || resp;
      this.selectedClientDetail = client;
      this._renderSelectedClient(client);
    } catch (e) {
      // Silently ignore — selectedClient déjà affiché avec données partielles
    }
  }

  _renderSelectedClient(client) {
    if (!this.hasSelectedClientTarget) return;
    this.selectedClientTarget.hidden = false;
    const typeLabel = client.type === "b2b" ? "B2B" : client.type === "b2c" ? "B2C" : "Administration";
    const parts = [client.legalName];
    const meta = [typeLabel];
    if (client.siret) meta.push(`SIRET ${client.siret}`);
    if (this.hasSelectedClientNameTarget) {
      this.selectedClientNameTarget.innerHTML = "";
      const strong = document.createElement("strong");
      strong.textContent = parts.join("");
      const sub = document.createElement("span");
      sub.className = "text-muted";
      sub.textContent = ` · ${meta.join(" · ")}`;
      this.selectedClientNameTarget.appendChild(strong);
      this.selectedClientNameTarget.appendChild(sub);
    }
    // Badges paiement (paymentScore)
    if (this.hasSelectedClientBadgesTarget) {
      this.selectedClientBadgesTarget.innerHTML = "";
      const score = client.paymentScore;
      if (score && Number.isFinite(score.averagePaymentDelayDays)) {
        const frag = this.cloneTemplate("tpl-badge-payment-delay");
        this.fillSlots(frag, { days: String(Math.round(score.averagePaymentDelayDays)) });
        this.selectedClientBadgesTarget.appendChild(frag);
      }
      if (score && score.badPayerBadge === true) {
        const frag = this.cloneTemplate("tpl-badge-bad-payer");
        this.selectedClientBadgesTarget.appendChild(frag);
      }
    }
  }

  toggleNewClient() {
    if (!this.hasNewClientFormTarget) return;
    const opened = this.newClientFormTarget.classList.toggle("is-open");
    if (this.hasNewClientToggleTarget) {
      this.newClientToggleTarget.setAttribute("aria-expanded", opened ? "true" : "false");
      this.newClientToggleTarget.textContent = opened ? "− Annuler nouveau client" : "+ Créer un nouveau client";
    }
    this._syncNewClientSiretVisibility();
    if (opened && this.hasNewClientNameTarget) this.newClientNameTarget.focus();
  }

  _syncNewClientSiretVisibility() {
    if (!this.hasNewClientTypeTarget || !this.hasNewClientSiretFieldTarget) return;
    const type = this.newClientTypeTarget.value;
    const needsSiret = type === "b2b" || type === "public_administration";
    this.newClientSiretFieldTarget.hidden = !needsSiret;
  }

  cancelNewClient() {
    if (!this.hasNewClientFormTarget) return;
    this.newClientFormTarget.classList.remove("is-open");
    if (this.hasNewClientToggleTarget) {
      this.newClientToggleTarget.setAttribute("aria-expanded", "false");
      this.newClientToggleTarget.textContent = "+ Créer un nouveau client";
    }
    this._resetNewClientForm();
  }

  _resetNewClientForm() {
    if (this.hasNewClientNameTarget) this.newClientNameTarget.value = "";
    if (this.hasNewClientSiretTarget) this.newClientSiretTarget.value = "";
    if (this.hasNewClientErrorTarget) {
      this.newClientErrorTarget.hidden = true;
      this.newClientErrorTarget.textContent = "";
    }
  }

  async createClient() {
    if (!this.hasNewClientTypeTarget || !this.hasNewClientNameTarget) return;
    this._syncNewClientSiretVisibility();
    const type = this.newClientTypeTarget.value;
    const legalName = this.newClientNameTarget.value.trim();
    const siret = this.hasNewClientSiretTarget ? this.newClientSiretTarget.value.trim().replace(/\s+/g, "") : "";

    if (!legalName) {
      this._showNewClientError("Le nom est obligatoire.");
      return;
    }
    if ((type === "b2b" || type === "public_administration") && siret.length !== 14) {
      this._showNewClientError("SIRET (14 chiffres) obligatoire pour ce type.");
      return;
    }

    const payload = { type, legalName };
    if (siret) payload.siret = siret;

    if (this.hasNewClientSubmitTarget) this.newClientSubmitTarget.disabled = true;
    try {
      const resp = await api.post("/clients", payload);
      const client = resp.data || resp;
      this.showSuccess(`Client « ${client.legalName} » créé.`);
      this._selectClient(client);
      this.cancelNewClient();
    } catch (e) {
      const msg = e instanceof APIError ? e.message : "Création client impossible.";
      this._showNewClientError(msg);
    } finally {
      if (this.hasNewClientSubmitTarget) this.newClientSubmitTarget.disabled = false;
    }
  }

  _showNewClientError(message) {
    if (!this.hasNewClientErrorTarget) return;
    this.newClientErrorTarget.textContent = message;
    this.newClientErrorTarget.hidden = false;
  }

  // ===========================================================================
  // Slide 4 — Article autocomplete + quick-create
  // ===========================================================================

  articleSearchInput(event) {
    if (this._articleSearchTimer) clearTimeout(this._articleSearchTimer);
    const q = event.target.value.trim();
    if (q.length < 2) {
      this._hideArticleResults();
      return;
    }
    this._articleSearchTimer = setTimeout(() => this._fetchArticles(q), SEARCH_DEBOUNCE_MS);
  }

  articleSearchBlur() {
    setTimeout(() => this._hideArticleResults(), 150);
  }

  articleSearchKeydown(event) {
    if (!this.hasArticleResultsTarget || this.articleResultsTarget.hidden) return;
    const options = Array.from(this.articleResultsTarget.querySelectorAll("[role='option']"));
    if (options.length === 0) return;
    const currentIdx = options.findIndex((o) => o.getAttribute("aria-selected") === "true");

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this._highlightOption(options, (currentIdx + 1) % options.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this._highlightOption(options, (currentIdx - 1 + options.length) % options.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const sel = options[currentIdx >= 0 ? currentIdx : 0];
      if (sel) sel.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    } else if (event.key === "Escape") {
      this._hideArticleResults();
    }
  }

  async _fetchArticles(q) {
    try {
      const resp = await api.get("/articles", {
        q,
        perPage: "10",
        "filter[status]": "active",
      });
      const items = Array.isArray(resp.data) ? resp.data : [];
      this._renderArticleResults(items);
    } catch (e) {
      this.showError("Recherche article impossible.");
    }
  }

  _renderArticleResults(articles) {
    if (!this.hasArticleResultsTarget) return;
    this.articleResultsTarget.innerHTML = "";
    if (articles.length === 0) {
      const empty = document.createElement("li");
      empty.className = "fs-12 text-muted";
      empty.style.padding = "10px 12px";
      empty.style.border = "1px solid var(--border)";
      empty.textContent = "Aucun article correspondant. Cliquez sur « + Créer un nouvel article ».";
      this.articleResultsTarget.appendChild(empty);
      this.articleResultsTarget.hidden = false;
      this.articleSearchTarget.setAttribute("aria-expanded", "true");
      return;
    }
    articles.forEach((a) => {
      const frag = this.cloneTemplate("tpl-article-result");
      const meta = [`TVA ${a.vatRate} %`, a.unit || ""].filter(Boolean).join(" · ");
      this.fillSlots(frag, {
        label: a.label,
        meta,
        price: formatEuros(a.priceExclVat),
      });
      const li = frag.querySelector("li");
      if (li) {
        li.dataset.articleId = a.id;
        li.addEventListener("mousedown", (ev) => {
          ev.preventDefault();
          this._addLineFromArticle(a);
        });
      }
      this.articleResultsTarget.appendChild(frag);
    });
    this.articleResultsTarget.hidden = false;
    this.articleSearchTarget.setAttribute("aria-expanded", "true");
  }

  _hideArticleResults() {
    if (!this.hasArticleResultsTarget) return;
    this.articleResultsTarget.hidden = true;
    if (this.hasArticleSearchTarget) this.articleSearchTarget.setAttribute("aria-expanded", "false");
  }

  _addLineFromArticle(article) {
    this.state.lines.push({
      id: newLineId(),
      articleId: article.id,
      activityId: article.activityId,
      label: article.label,
      qty: "1",
      unit: article.unit || "unité",
      unitPrice: article.priceExclVat,
      vatRate: article.vatRate,
    });
    if (this.hasArticleSearchTarget) this.articleSearchTarget.value = "";
    this._hideArticleResults();
    this._renderLines();
    this._renderTotals();
    this._scheduleSave();
  }

  addFreeLine() {
    const activityId = this.activeActivities.length === 1 ? this.activeActivities[0].id : null;
    this.state.lines.push({
      id: newLineId(),
      articleId: null,
      activityId,
      label: "",
      qty: "1",
      unit: "unité",
      unitPrice: "0",
      vatRate: "0",
    });
    this._renderLines();
    this._renderTotals();
    this._scheduleSave();
  }

  _renderLines() {
    if (!this.hasLinesContainerTarget) return;
    // Supprimer les lignes existantes (.nm-lines__row[data-line]) sans toucher au head et add-row
    this.linesContainerTarget.querySelectorAll(".nm-lines__row[data-line]").forEach((el) => el.remove());

    const addRow = this.linesContainerTarget.querySelector(".nm-lines__add");
    this.state.lines.forEach((line) => {
      const frag = this.cloneTemplate("tpl-line-row");
      const row = frag.querySelector(".nm-lines__row");
      if (!row) return;
      row.dataset.lineId = line.id;

      const labelInput = row.querySelector("[data-slot='label']");
      const qtyInput = row.querySelector("[data-slot='qty']");
      const priceInput = row.querySelector("[data-slot='unitPrice']");
      const totalInput = row.querySelector("[data-slot='totalHt']");
      const metaSpan = row.querySelector("[data-slot='meta']");
      const delBtn = row.querySelector("[data-line-del]");

      if (labelInput) {
        labelInput.value = line.label;
        labelInput.addEventListener("input", (e) => {
          line.label = e.target.value;
          this._scheduleSave();
        });
      }
      if (qtyInput) {
        qtyInput.value = line.qty;
        qtyInput.addEventListener("input", (e) => {
          line.qty = e.target.value;
          totalInput.value = formatEuros(this._lineHt(line));
          this._renderTotals();
          this._scheduleSave();
        });
      }
      if (priceInput) {
        priceInput.value = line.unitPrice;
        priceInput.addEventListener("input", (e) => {
          line.unitPrice = e.target.value;
          totalInput.value = formatEuros(this._lineHt(line));
          this._renderTotals();
          this._scheduleSave();
        });
      }
      if (totalInput) totalInput.value = formatEuros(this._lineHt(line));

      // Meta : badge activité (verrouillé si article) ou drop-down (libre + N>1)
      if (metaSpan) {
        metaSpan.innerHTML = "";
        if (line.articleId) {
          // Article catalogue → badge verrouillé
          const badge = this.cloneTemplate("tpl-badge-activity");
          const activity = this.activitiesById.get(line.activityId);
          this.fillSlots(badge, { name: activity?.label || activity?.description || "Activité" });
          metaSpan.appendChild(badge);
          metaSpan.append(` TVA ${line.vatRate} % · article catalogue (activité verrouillée)`);
        } else if (this.activeActivities.length === 1) {
          // 1 seule activité → masquée
          metaSpan.append(`TVA ${line.vatRate} % · ligne libre`);
        } else {
          // Plusieurs activités → drop-down obligatoire
          const select = document.createElement("select");
          select.className = "nm-select";
          select.style.padding = "2px 6px";
          select.style.fontSize = "12px";
          select.style.maxWidth = "220px";
          select.setAttribute("aria-label", "Activité de la ligne libre");
          const placeholder = document.createElement("option");
          placeholder.value = "";
          placeholder.textContent = "— Choisir l'activité —";
          select.appendChild(placeholder);
          this.activeActivities.forEach((a) => {
            const opt = document.createElement("option");
            opt.value = a.id;
            opt.textContent = a.label || a.description || "Activité";
            if (a.id === line.activityId) opt.selected = true;
            select.appendChild(opt);
          });
          select.addEventListener("change", (e) => {
            line.activityId = e.target.value || null;
            this._renderTotals();
            this._scheduleSave();
          });
          metaSpan.appendChild(select);
          metaSpan.append(" ligne libre (activité obligatoire)");
        }
      }
      if (delBtn) {
        delBtn.addEventListener("click", () => {
          this.state.lines = this.state.lines.filter((l) => l.id !== line.id);
          this._renderLines();
          this._renderTotals();
          this._scheduleSave();
        });
      }

      this.linesContainerTarget.insertBefore(row, addRow);
    });
  }

  _lineHt(line) {
    const q = Number(line.qty);
    const p = Number(line.unitPrice);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return 0;
    return q * p;
  }

  _renderTotals() {
    let totalHt = 0;
    const vatByRate = new Map();        // rate(number) → totalVat
    const htByActivity = new Map();     // activityId → totalHt

    this.state.lines.forEach((line) => {
      const ht = this._lineHt(line);
      totalHt += ht;
      const rate = Number(line.vatRate);
      if (Number.isFinite(rate)) {
        const vat = ht * rate / 100;
        vatByRate.set(rate, (vatByRate.get(rate) || 0) + vat);
      }
      if (line.activityId) {
        htByActivity.set(line.activityId, (htByActivity.get(line.activityId) || 0) + ht);
      }
    });

    let totalVat = 0;
    vatByRate.forEach((v) => { totalVat += v; });

    if (this.hasTotalsHtTarget) this.totalsHtTarget.textContent = formatEuros(totalHt);
    if (this.hasTotalsTtcTarget) this.totalsTtcTarget.textContent = formatEuros(totalHt + totalVat);

    if (this.hasTotalsVatBreakdownTarget) {
      if (vatByRate.size === 0) {
        this.totalsVatBreakdownTarget.textContent = "—";
      } else {
        const parts = [];
        Array.from(vatByRate.entries())
          .sort((a, b) => b[0] - a[0])
          .forEach(([rate, vat]) => {
            const rateLabel = String(rate).replace(".", ",");
            parts.push(`TVA ${rateLabel} % = ${formatEuros(vat)}`);
          });
        this.totalsVatBreakdownTarget.textContent = parts.join(" · ");
      }
    }

    // Ventilation activité : visible si org N>1 ET ≥2 activités distinctes sur les lignes
    if (this.hasTotalsActivityBreakdownCardTarget) {
      const showBreakdown = this.activeActivities.length > 1 && htByActivity.size >= 2;
      this.totalsActivityBreakdownCardTarget.hidden = !showBreakdown;
      if (showBreakdown && this.hasTotalsActivityBreakdownTarget) {
        this.totalsActivityBreakdownTarget.innerHTML = "";
        htByActivity.forEach((amount, activityId) => {
          const activity = this.activitiesById.get(activityId);
          const li = document.createElement("li");
          li.className = "flex items-center";
          li.style.display = "flex";
          li.style.justifyContent = "space-between";
          li.style.padding = "8px 0";
          li.style.borderTop = "1px solid var(--border)";
          const left = document.createElement("span");
          left.className = "flex items-center gap-12";
          const badge = this.cloneTemplate("tpl-badge-activity");
          this.fillSlots(badge, { name: activity?.label || activity?.description || "Activité" });
          left.appendChild(badge);
          const right = document.createElement("span");
          right.className = "fs-13";
          right.style.fontFamily = "var(--mono)";
          right.textContent = formatEuros(amount);
          li.appendChild(left);
          li.appendChild(right);
          this.totalsActivityBreakdownTarget.appendChild(li);
        });
      }
    }
  }

  // ===========================================================================
  // Quick-create article (slide 4)
  // ===========================================================================

  _populateNewArticleActivities() {
    if (!this.hasNewArticleActivityTarget) return;
    this.newArticleActivityTarget.innerHTML = "";
    if (this.activeActivities.length === 1) {
      // Masque le champ
      if (this.hasNewArticleActivityFieldTarget) this.newArticleActivityFieldTarget.hidden = true;
      const opt = document.createElement("option");
      opt.value = this.activeActivities[0].id;
      opt.textContent = this.activeActivities[0].label || "Activité unique";
      opt.selected = true;
      this.newArticleActivityTarget.appendChild(opt);
      return;
    }
    if (this.hasNewArticleActivityFieldTarget) this.newArticleActivityFieldTarget.hidden = false;
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "— Choisir l'activité —";
    this.newArticleActivityTarget.appendChild(placeholder);
    this.activeActivities.forEach((a) => {
      const opt = document.createElement("option");
      opt.value = a.id;
      opt.textContent = a.label || a.description || "Activité";
      this.newArticleActivityTarget.appendChild(opt);
    });
  }

  toggleNewArticle() {
    if (!this.hasNewArticleFormTarget) return;
    const hidden = this.newArticleFormTarget.hidden;
    this.newArticleFormTarget.hidden = !hidden;
    if (this.hasNewArticleToggleTarget) {
      this.newArticleToggleTarget.setAttribute("aria-expanded", hidden ? "true" : "false");
    }
    if (hidden && this.hasNewArticleLabelTarget) this.newArticleLabelTarget.focus();
  }

  cancelNewArticle() {
    if (!this.hasNewArticleFormTarget) return;
    this.newArticleFormTarget.hidden = true;
    if (this.hasNewArticleToggleTarget) this.newArticleToggleTarget.setAttribute("aria-expanded", "false");
    this._resetNewArticleForm();
  }

  _resetNewArticleForm() {
    if (this.hasNewArticleLabelTarget) this.newArticleLabelTarget.value = "";
    if (this.hasNewArticleUnitTarget) this.newArticleUnitTarget.value = "";
    if (this.hasNewArticlePriceTarget) this.newArticlePriceTarget.value = "";
    if (this.hasNewArticleErrorTarget) {
      this.newArticleErrorTarget.hidden = true;
      this.newArticleErrorTarget.textContent = "";
    }
  }

  async createArticle() {
    if (!this.hasNewArticleLabelTarget) return;
    const label = this.newArticleLabelTarget.value.trim();
    const unit = this.hasNewArticleUnitTarget ? this.newArticleUnitTarget.value.trim() : "";
    const price = this.hasNewArticlePriceTarget ? this.newArticlePriceTarget.value.trim() : "";
    const vatRaw = this.hasNewArticleVatTarget ? this.newArticleVatTarget.value : "0";
    const vatNum = Number(vatRaw);
    const activityId = this.hasNewArticleActivityTarget ? this.newArticleActivityTarget.value : "";

    if (!label) return this._showNewArticleError("Le libellé est obligatoire.");
    if (!unit) return this._showNewArticleError("L'unité est obligatoire.");
    if (price === "" || !Number.isFinite(Number(price))) return this._showNewArticleError("Le prix HT est invalide.");
    if (!VAT_RATES.includes(vatNum)) return this._showNewArticleError("Taux TVA invalide.");
    if (!activityId) return this._showNewArticleError("L'activité est obligatoire.");

    try {
      const resp = await api.post("/articles", {
        label,
        unit,
        priceExclVat: price,
        vatRate: vatNum,
        activityId,
      });
      const article = resp.data || resp;
      this.showSuccess(`Article « ${article.label} » créé et ajouté.`);
      this._addLineFromArticle(article);
      this.cancelNewArticle();
    } catch (e) {
      const msg = e instanceof APIError ? e.message : "Création article impossible.";
      this._showNewArticleError(msg);
    }
  }

  _showNewArticleError(message) {
    if (!this.hasNewArticleErrorTarget) return;
    this.newArticleErrorTarget.textContent = message;
    this.newArticleErrorTarget.hidden = false;
  }

  // ===========================================================================
  // Pièces jointes
  // ===========================================================================

  _applyDefaultAttachments() {
    const isQuote = this.state.type === "quote";
    const defaults = isQuote ? this.defaultAttachments.quotes : this.defaultAttachments.invoices;
    (defaults || []).forEach((id) => this.state.attachedDocumentIds.add(id));
  }

  _renderAttachmentsList() {
    if (!this.hasAttachmentsListTarget) return;
    this.attachmentsListTarget.innerHTML = "";
    const docs = Array.from(this.state.attachedDocumentIds)
      .map((id) => this.documentsById.get(id))
      .filter((d) => d !== undefined);
    if (docs.length === 0) {
      const empty = document.createElement("li");
      empty.className = "fs-12 text-muted";
      empty.style.padding = "12px 0";
      empty.textContent = "Aucune pièce jointe — utilisez « + Voir tout » pour ajouter.";
      this.attachmentsListTarget.appendChild(empty);
      return;
    }
    docs.forEach((doc) => {
      const frag = this.cloneTemplate("tpl-attached-doc-item");
      const meta = [doc.category || "Document"];
      if (doc.isPublic === false) meta.push("privé");
      this.fillSlots(frag, { label: doc.label || doc.name || "Document", meta: meta.join(" · ") });
      const checkbox = frag.querySelector("input[type='checkbox']");
      const labelEl = frag.querySelector("label");
      if (checkbox) {
        checkbox.id = `w-att-${doc.id}`;
        checkbox.checked = true;
        checkbox.dataset.documentId = doc.id;
      }
      if (labelEl && checkbox) labelEl.setAttribute("for", checkbox.id);
      this.attachmentsListTarget.appendChild(frag);
    });
  }

  toggleAttachment(event) {
    const target = event.target;
    if (!target || target.type !== "checkbox") return;
    const docId = target.dataset.documentId;
    if (!docId) return;
    if (target.checked) this.state.attachedDocumentIds.add(docId);
    else this.state.attachedDocumentIds.delete(docId);
    this._renderAttachmentsList();
    this._scheduleSave();
  }

  toggleAttachmentFromModal(event) {
    const target = event.target;
    if (!target || target.type !== "checkbox") return;
    const docId = target.dataset.documentId;
    if (!docId) return;
    if (target.checked) this.state.attachedDocumentIds.add(docId);
    else this.state.attachedDocumentIds.delete(docId);
    this._renderAttachmentsList();
    this._scheduleSave();
  }

  openDocumentsModal() {
    if (!this.hasDocumentsModalTarget) return;
    this._renderDocumentsModalList();
    this.documentsModalTarget.hidden = false;
    if (this.hasDocumentsModalSearchTarget) {
      this.documentsModalSearchTarget.value = "";
      this.documentsModalSearchTarget.focus();
    }
    document.addEventListener("keydown", this._modalKeydown);
  }

  closeDocumentsModal() {
    if (!this.hasDocumentsModalTarget) return;
    this.documentsModalTarget.hidden = true;
    document.removeEventListener("keydown", this._modalKeydown);
  }

  _modalKeydown = (e) => {
    if (e.key === "Escape") this.closeDocumentsModal();
  };

  filterDocumentsModal(event) {
    const q = event.target.value.trim().toLowerCase();
    this._renderDocumentsModalList(q);
  }

  _renderDocumentsModalList(filter = "") {
    if (!this.hasDocumentsModalListTarget) return;
    this.documentsModalListTarget.innerHTML = "";
    const docs = Array.from(this.documentsById.values()).filter((d) => {
      if (!filter) return true;
      const haystack = `${d.label || d.name || ""} ${d.category || ""}`.toLowerCase();
      return haystack.includes(filter);
    });
    if (docs.length === 0) {
      const empty = document.createElement("li");
      empty.className = "fs-12 text-muted";
      empty.style.padding = "10px 0";
      empty.textContent = "Aucun document ne correspond.";
      this.documentsModalListTarget.appendChild(empty);
      return;
    }
    docs.forEach((doc) => {
      const frag = this.cloneTemplate("tpl-doc-modal-row");
      const meta = [doc.category || "Document"];
      if (doc.isPublic === false) meta.push("privé");
      this.fillSlots(frag, {
        label: doc.label || doc.name || "Document",
        meta: meta.join(" · "),
        size: doc.sizeBytes ? `${Math.round(doc.sizeBytes / 1024)} Ko` : "",
      });
      const checkbox = frag.querySelector("input[type='checkbox']");
      const labelEl = frag.querySelector("label");
      if (checkbox) {
        checkbox.id = `w-modal-${doc.id}`;
        checkbox.checked = this.state.attachedDocumentIds.has(doc.id);
        checkbox.dataset.documentId = doc.id;
      }
      if (labelEl && checkbox) labelEl.setAttribute("for", checkbox.id);
      this.documentsModalListTarget.appendChild(frag);
    });
  }

  // ===========================================================================
  // Slide 3 — Suggestions intelligentes (EPIC-011.5)
  // ===========================================================================

  async _fetchSuggestionsForClient(clientId) {
    // Skip pour les avoirs : héritage des lignes via slide 1bis.
    if (this.state.type === "credit_note") {
      this.state.suggestions = { topArticles: [], lastDocuments: [], skip: true };
      this._renderSuggestions();
      this._renderStepHeader();
      return;
    }
    try {
      const resp = await api.get(`/clients/${clientId}/wizard-suggestions`);
      const data = resp.data || resp;
      const topArticles = Array.isArray(data.topArticles) ? data.topArticles : [];
      const lastDocuments = Array.isArray(data.lastDocuments) ? data.lastDocuments : [];
      this.state.suggestions = {
        topArticles,
        lastDocuments,
        skip: topArticles.length === 0 && lastDocuments.length === 0,
      };
    } catch (e) {
      // Sur erreur, on saute la slide pour ne pas bloquer le wizard.
      this.state.suggestions = { topArticles: [], lastDocuments: [], skip: true };
    }
    this._renderSuggestions();
    this._renderStepHeader();
    this._scheduleSave();
  }

  _renderSuggestions() {
    if (this.hasTopArticlesListTarget) {
      this.topArticlesListTarget.innerHTML = "";
      this.state.suggestions.topArticles.forEach((article, idx) => {
        const frag = this.cloneTemplate("tpl-suggested-article");
        const metaBits = [];
        if (article.unit) metaBits.push(article.unit);
        const rateLabel = String(article.vatRate ?? "0").replace(".", ",");
        metaBits.push(`TVA ${rateLabel} %`);
        if (Number.isFinite(Number(article.usageCount))) {
          const n = Number(article.usageCount);
          metaBits.push(`facturé ${n} fois`);
        }
        this.fillSlots(frag, {
          label: article.label || "(sans libellé)",
          meta: metaBits.join(" · "),
          price: formatEuros(article.priceExclVat),
        });
        const btn = frag.querySelector("[data-slot-action='add']");
        if (btn) {
          btn.dataset.suggestionIndex = String(idx);
          btn.setAttribute("aria-label", `Ajouter ${article.label || "article"} aux lignes`);
        }
        this.topArticlesListTarget.appendChild(frag);
      });
    }

    if (this.hasLastDocumentsListTarget) {
      this.lastDocumentsListTarget.innerHTML = "";
      this.state.suggestions.lastDocuments.forEach((doc, idx) => {
        const frag = this.cloneTemplate("tpl-suggested-document");
        const typeLabel = doc.type === "quote" ? "devis" : "facture";
        const dateLabel = formatDateFR(doc.date);
        this.fillSlots(frag, {
          number: doc.id ? `${typeLabel === "devis" ? "Devis" : "Facture"} ${doc.id.slice(0, 8)}` : "(sans numéro)",
          meta: `Émis le ${dateLabel} · ${typeLabel} · ${formatEuros(doc.amountInclVat)} TTC`,
          lines: "",
        });
        const btn = frag.querySelector("[data-slot-action='resume']");
        if (btn) {
          btn.dataset.suggestionIndex = String(idx);
          btn.setAttribute("aria-label", `Reprendre le contenu du ${typeLabel}`);
        }
        this.lastDocumentsListTarget.appendChild(frag);
      });
    }
  }

  applyArticleSuggestion(event) {
    const btn = event.target.closest("[data-slot-action='add']");
    if (!btn) return;
    const idx = Number(btn.dataset.suggestionIndex);
    const article = this.state.suggestions.topArticles[idx];
    if (!article) return;
    this._addLineFromArticle({
      id: article.articleId,
      activityId: article.activityId,
      label: article.label,
      unit: article.unit || "unité",
      priceExclVat: article.priceExclVat,
      vatRate: article.vatRate,
    });
    this.showSuccess(`« ${article.label} » ajouté aux lignes.`);
  }

  async applyDocumentSuggestion(event) {
    const btn = event.target.closest("[data-slot-action='resume']");
    if (!btn) return;
    const idx = Number(btn.dataset.suggestionIndex);
    const doc = this.state.suggestions.lastDocuments[idx];
    if (!doc) return;

    if (this.state.lines.length > 0) {
      const confirmed = await this.confirm(
        "Remplacer les lignes existantes par celles du document sélectionné ?",
        { confirmLabel: "Remplacer", cancelLabel: "Annuler" },
      );
      if (!confirmed) return;
    }

    const path = doc.type === "quote" ? `/quotes/${doc.id}` : `/invoices/${doc.id}`;
    try {
      const resp = await api.get(path);
      const sourceDoc = resp.data || resp;
      const sourceLines = Array.isArray(sourceDoc.lines) ? sourceDoc.lines : [];
      this.state.lines = sourceLines.map((l) => ({
        id: newLineId(),
        articleId: l.articleId || null,
        activityId: l.activityId,
        label: l.label,
        qty: l.quantity,
        unit: l.unit || "unité",
        unitPrice: l.priceExclVat,
        vatRate: l.vatRate,
      }));
      this._renderLines();
      this._renderTotals();
      this._scheduleSave();
      this.showSuccess("Lignes reprises du document source.");
    } catch (e) {
      const msg = e instanceof APIError ? e.message : "Impossible de récupérer le contenu de ce document.";
      this.showError(msg);
    }
  }

  // ===========================================================================
  // Slide 5 — Cadence & options de récurrence (EPIC-011.5)
  // ===========================================================================

  async _fetchOrgSettingsForRecurrence() {
    if (this._orgSettingsLoaded) return;
    try {
      const resp = await api.get("/organization/settings");
      const settings = resp.data || resp;
      const includeOnInvoices = settings?.paymentProvider?.includeOnInvoices;
      if (typeof includeOnInvoices === "boolean") {
        this.state.recurrence.integratedPayment = includeOnInvoices;
      }
      this._orgSettingsLoaded = true;
      this._syncRecurrenceInputs();
    } catch (e) {
      // Garde la valeur par défaut state.recurrence.integratedPayment = true.
    }
  }

  _syncRecurrenceInputs() {
    const r = this.state.recurrence;
    if (this.hasRecurrenceIntervalMonthsTarget) this.recurrenceIntervalMonthsTarget.value = String(r.intervalMonths);
    if (this.hasRecurrenceDayOfMonthTarget) this.recurrenceDayOfMonthTarget.value = String(r.dayOfMonth);
    if (this.hasRecurrenceStartDateTarget) {
      this.recurrenceStartDateTarget.value = r.startDate;
      this.recurrenceStartDateTarget.min = new Date().toISOString().slice(0, 10);
    }
    if (this.hasRecurrenceEndDateTarget) this.recurrenceEndDateTarget.value = r.endDate;
    this.recurrenceModeTargets.forEach((input) => {
      input.checked = input.value === r.generationMode;
    });
    if (this.hasRecurrencePaymentTarget) this.recurrencePaymentTarget.checked = !!r.integratedPayment;
    if (this.hasRecurrenceRemindersTarget) this.recurrenceRemindersTarget.checked = !!r.autoReminders;
    this.recurrenceAttachmentsTargets.forEach((input) => {
      input.checked = input.value === r.attachmentsMode;
    });
  }

  recurrenceFieldChanged(event) {
    const target = event.target;
    if (!target) return;
    if (target === this.recurrenceIntervalMonthsTarget) {
      const v = parseInt(target.value, 10);
      this.state.recurrence.intervalMonths = Number.isFinite(v) ? v : 0;
    } else if (target === this.recurrenceDayOfMonthTarget) {
      const v = parseInt(target.value, 10);
      this.state.recurrence.dayOfMonth = Number.isFinite(v) ? v : 0;
    } else if (target === this.recurrenceStartDateTarget) {
      this.state.recurrence.startDate = target.value;
    } else if (target === this.recurrenceEndDateTarget) {
      this.state.recurrence.endDate = target.value;
    } else if (target.name === "w-rec-mode") {
      this.state.recurrence.generationMode = target.value;
    } else if (target === this.recurrencePaymentTarget) {
      this.state.recurrence.integratedPayment = !!target.checked;
    } else if (target === this.recurrenceRemindersTarget) {
      this.state.recurrence.autoReminders = !!target.checked;
    }
    this._scheduleSave();
    this._schedulePreviewDates();
  }

  recurrenceAttachmentsChanged(event) {
    const target = event.target;
    if (!target || target.type !== "radio") return;
    const mode = target.value;
    this.state.recurrence.attachmentsMode = mode;
    if (mode === "frozen") {
      this.state.recurrence.frozenAttachmentIds = Array.from(this.state.attachedDocumentIds);
    } else {
      this.state.recurrence.frozenAttachmentIds = [];
    }
    this._scheduleSave();
  }

  _schedulePreviewDates() {
    if (this._previewTimer) clearTimeout(this._previewTimer);
    this._previewTimer = setTimeout(() => this._previewRecurrenceDates(), PREVIEW_DEBOUNCE_MS);
  }

  async _previewRecurrenceDates() {
    if (!this.hasPreviewDatesTarget) return;
    const r = this.state.recurrence;
    if (!r.startDate || !r.intervalMonths || !r.dayOfMonth) {
      this._renderPreviewHint("Réglages incomplets ou hors plage");
      return;
    }
    const body = {
      intervalMonths: r.intervalMonths,
      dayOfMonth: r.dayOfMonth,
      startDate: r.startDate,
    };
    if (r.endDate) body.endDate = r.endDate;
    try {
      const resp = await api.post("/recurring-invoices/preview-dates", body);
      const data = resp.data || resp;
      const dates = Array.isArray(data.nextDates) ? data.nextDates : [];
      this._renderPreviewDates(dates);
    } catch (e) {
      this._renderPreviewHint("Réglages incomplets ou hors plage");
    }
  }

  _renderPreviewDates(dates) {
    this.previewDatesTarget.innerHTML = "";
    if (dates.length === 0) {
      this._renderPreviewHint("Aucune date à venir dans la période choisie.");
      return;
    }
    const ttc = this._computeTotalTtc();
    const ttcLabel = `${formatEuros(ttc)} TTC`;
    dates.forEach((iso) => {
      const frag = this.cloneTemplate("tpl-preview-date-row");
      this.fillSlots(frag, {
        date: formatRecurrenceDate(iso),
        amount: ttcLabel,
      });
      this.previewDatesTarget.appendChild(frag);
    });
  }

  _renderPreviewHint(message) {
    this.previewDatesTarget.innerHTML = "";
    const li = document.createElement("li");
    li.className = "fs-13 text-muted";
    li.style.padding = "12px 0";
    li.textContent = message;
    this.previewDatesTarget.appendChild(li);
  }

  _computeTotalTtc() {
    let totalHt = 0;
    let totalVat = 0;
    this.state.lines.forEach((line) => {
      const ht = this._lineHt(line);
      totalHt += ht;
      const rate = Number(line.vatRate);
      if (Number.isFinite(rate)) totalVat += ht * rate / 100;
    });
    return totalHt + totalVat;
  }

  _showRecurrenceError(message) {
    if (!this.hasRecurrenceErrorTarget) return;
    this.recurrenceErrorTarget.textContent = message;
    this.recurrenceErrorTarget.hidden = false;
  }

  _hideRecurrenceError() {
    if (!this.hasRecurrenceErrorTarget) return;
    this.recurrenceErrorTarget.hidden = true;
    this.recurrenceErrorTarget.textContent = "";
  }

  async _submitRecurringInvoice() {
    this._hideRecurrenceError();
    if (!this.state.clientId) {
      this._showRecurrenceError("Veuillez sélectionner un client (étape 2).");
      return;
    }
    if (this.state.lines.length === 0) {
      this._showRecurrenceError("Ajoutez au moins une ligne (étape 4).");
      return;
    }
    const r = this.state.recurrence;
    if (!r.startDate) {
      this._showRecurrenceError("Date de début obligatoire.");
      return;
    }

    const linesPayload = this.state.lines.map((line) => {
      const item = {
        label: line.label,
        quantity: String(line.qty),
        unit: line.unit || "unité",
        priceExclVat: String(line.unitPrice),
        vatRate: String(line.vatRate),
      };
      if (line.articleId) item.articleId = line.articleId;
      if (line.activityId) item.activityId = line.activityId;
      return item;
    });

    const payload = {
      clientId: this.state.clientId,
      intervalMonths: r.intervalMonths,
      dayOfMonth: r.dayOfMonth,
      startDate: r.startDate,
      generationMode: r.generationMode,
      integratedPayment: !!r.integratedPayment,
      autoReminders: !!r.autoReminders,
      attachmentsMode: r.attachmentsMode,
      frozenAttachmentIds: r.attachmentsMode === "frozen" ? r.frozenAttachmentIds : [],
      lines: linesPayload,
      attachedDocumentIds: Array.from(this.state.attachedDocumentIds),
    };
    if (r.endDate) payload.endDate = r.endDate;

    const setBusy = (busy) => {
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = busy;
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = busy;
    };

    setBusy(true);
    try {
      await api.post("/recurring-invoices", payload);
      this._clearStorage();
      window.location.assign("/factures.html?recurrence_created=1");
    } catch (e) {
      const msg = e instanceof APIError ? e.message : "Création de la récurrence impossible.";
      this._showRecurrenceError(msg);
    } finally {
      setBusy(false);
    }
  }

  // ===========================================================================
  // Submit (Enregistrer brouillon / Valider et continuer)
  // ===========================================================================

  async saveDraft() {
    if (this.state.type === "recurring") {
      // Pour la récurrence, pas de notion de brouillon serveur (création
      // directe en `status='active'`). On suit le même flow que « Valider ».
      await this._submitRecurringInvoice();
      return;
    }
    await this._submitDraft(/* andContinue */ false);
  }

  async submit() {
    if (this.state.type === "recurring") {
      await this._submitRecurringInvoice();
      return;
    }
    await this._submitDraft(/* andContinue */ true);
  }

  async _submitDraft() {
    if (!this.state.clientId) {
      this.showError("Veuillez sélectionner un client (étape 2).");
      return;
    }
    if (this.state.lines.length === 0) {
      this.showError("Ajoutez au moins une ligne (étape 4).");
      return;
    }

    const linesPayload = this.state.lines.map((line) => {
      const item = {
        label: line.label,
        quantity: String(line.qty),
        unit: line.unit || "unité",
        priceExclVat: String(line.unitPrice),
        vatRate: String(line.vatRate),
      };
      if (line.articleId) item.articleId = line.articleId;
      if (line.activityId) item.activityId = line.activityId;
      return item;
    });
    const attachedDocumentIds = Array.from(this.state.attachedDocumentIds);

    const setBusy = (busy) => {
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = busy;
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = busy;
    };

    setBusy(true);
    try {
      let endpoint;
      let payload;
      if (this.state.type === "quote") {
        endpoint = "/quotes/draft";
        payload = {
          clientId: this.state.clientId,
          lines: linesPayload,
          attachedDocumentIds,
        };
      } else {
        endpoint = "/invoices/draft";
        const kind = TYPE_TO_INVOICE_KIND[this.state.type] || "standard";
        payload = {
          kind,
          clientId: this.state.clientId,
          lines: linesPayload,
          attachedDocumentIds,
        };
        if (kind === "credit_note") {
          if (!this.state.sourceInvoiceId) {
            this.showError("Facture source obligatoire pour un avoir.");
            setBusy(false);
            return;
          }
          payload.sourceInvoiceId = this.state.sourceInvoiceId;
        }
      }

      const resp = await api.post(endpoint, payload);
      const doc = resp.data || resp;
      this.showSuccess("Brouillon enregistré.");
      this._clearStorage();
      // Redirige vers la liste correspondante (placeholders 11.5)
      const targetUrl = this.state.type === "quote" ? "/devis.html" : "/factures.html";
      window.location.assign(targetUrl);
    } catch (e) {
      const msg = e instanceof APIError ? e.message : "Enregistrement impossible.";
      this.showError(msg);
    } finally {
      setBusy(false);
    }
  }

  // ===========================================================================
  // Abandon
  // ===========================================================================

  async abandon(event) {
    const confirmed = await this.confirm(
      "Abandonner ce brouillon ? Toutes les saisies non enregistrées seront perdues.",
      { confirmLabel: "Abandonner", cancelLabel: "Continuer la saisie" },
    );
    if (!confirmed) {
      event.preventDefault();
      return;
    }
    this._clearStorage();
    // Le href de l'ancre fera la navigation
  }
}
